import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import { HashLink } from 'react-router-hash-link';
import { AppLink } from '../../components/ui/AppLink';
import { H3, PageArea, TableArea } from '../../components/ui/StyledComponents';
import {
  LocalizedIdWithPath,
  ParentPathModel,
  useParentPathModel,
} from '../../features/ParentPathModel';
import { PathData } from '../../features/PathModel';
import { useI18n } from '../../hooks/I18n';
import { usePageTitleUpdateEffect } from '../../hooks/PageUtil';
import { useData } from '../../hooks/UseData';
import { getLogger } from '../../utils/Logger';

const logger = getLogger(import.meta.url);

type IndexTableProps = {
  model: ParentPathModel;
};

const IndexTable: React.FC<IndexTableProps> = ({ model }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 1 }}>
    {model.groups.map(({ groupId: { id, displayName } }) => (
      <Box key={id} sx={{ whiteSpace: 'nowrap', px: 1 }}>
        <HashLink to={`#${id}`}>{displayName}</HashLink>
      </Box>
    ))}
  </Box>
);

const StyledGrid: React.FC<{ items: LocalizedIdWithPath[] }> = ({ items }) => (
  <Grid
    container
    columns={{ xs: 4, sm: 8, md: 12, lg: 16 }}
    sx={{ p: 3 }}
    rowSpacing={1}
    columnSpacing={1}
  >
    {items.map(({ id, displayName, path }) => (
      <Grid item key={id} xs={2} sm={4} md={4}>
        <Typography variant="body2">
          <AppLink to={path}>{displayName}</AppLink>
        </Typography>
      </Grid>
    ))}
  </Grid>
);

const ListPageForFew: React.FC<{ model: ParentPathModel }> = ({ model }) => (
  <StyledGrid items={model.getLocalizedIdWithPath()} />
);

const ListPageForMany: React.FC<{ model: ParentPathModel }> = ({ model }) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const hash = window.location.hash.replace('#', '');
  React.useEffect(
    () => elementRef?.current?.scrollIntoView(),
    [elementRef?.current]
  );

  return (
    <>
      <IndexTable model={model} />
      {model.groups.map(({ groupId, subGroups }) => (
        <TableArea
          id={groupId.id}
          key={groupId.id}
          title={groupId.displayName}
          ref={groupId.id === hash ? elementRef : null}
        >
          {subGroups.map(({ subGroupId: { id, displayName }, items }) => {
            const element = <StyledGrid key={id} items={items} />;
            if (id.length > 0) {
              return (
                <H3 key={id} title={displayName}>
                  {element}
                </H3>
              );
            } else {
              return element;
            }
          })}
        </TableArea>
      ))}
    </>
  );
};

type ListPageProps = {
  parentPath: string;
  paths: string[];
};

const ListPage: React.FC<ListPageProps> = ({ parentPath, paths }) => {
  // /people/people/
  logger.debug('START');
  const i18n = useI18n();
  const { fetchRaceData } = useData();
  const raceData = fetchRaceData();
  const pageTitle = new PathData(parentPath).localizeDirName(
    i18n,
    raceData
  ).displayName;
  usePageTitleUpdateEffect(pageTitle);
  const parentPathModel = useParentPathModel(parentPath);

  return React.useMemo(
    () => (
      <PageArea title={pageTitle}>
        {paths.length > 30 ? (
          <ListPageForMany model={parentPathModel} />
        ) : (
          <ListPageForFew model={parentPathModel} />
        )}
      </PageArea>
    ),
    []
  );
};

export default React.memo(ListPage);
